import { DatePicker, Dropdown, Section, AutoComplete } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { Typography, TextField as MUITextField, Chip } from '@material-ui/core';
import type { AtualizarCoordenadoresRequest, ContraparteRecebedoraEnum, ParticipanteResponse } from 'common/genericTypes';
import { addCpfCnpjMask } from 'utils/mask';
import { stripCpfCnpjFormatting } from 'utils/validation';
import ParticipanteAutoComplete from 'components/participante/participanteAutoComplete';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addAlert } from 'store/modules/snackBar';
import CoordenadoresConfirmationDialog from './coordenadoresConfirmationDialog';

interface DistribuicaoPublicaSectionProps {
  showDistribuicaoPublica: boolean;
  labels: any;
  tipoDistribuicaoPublica: any[];
  caracteristicas: any;
  updateField: Function;
  dataEncerramentoDistribuicao: any;
  coordenadoresOptions: ParticipanteResponse[];
  coordenadoresIds: string[];
  coordenadorLiderId: string;
  escrituradores: ParticipanteResponse[];
  escriturador: ParticipanteResponse | null;
  contraparteRecebedora: any[];
  handleEscrituradorChange: (value: ParticipanteResponse | null) => void;
  showError: (field: string) => boolean;
  codigo?: string;
  delimitacaoEfetuada?: boolean;
  escrituradorDisabled?: boolean;
}

const DistribuicaoPublicaSection: React.FC<DistribuicaoPublicaSectionProps> = ({
  showDistribuicaoPublica,
  labels,
  tipoDistribuicaoPublica,
  caracteristicas,
  updateField,
  dataEncerramentoDistribuicao,
  coordenadoresOptions,
  coordenadoresIds,
  coordenadorLiderId,
  escrituradores,
  escriturador,
  contraparteRecebedora,
  handleEscrituradorChange,
  showError,
  codigo,
  delimitacaoEfetuada = false,
  escrituradorDisabled = false
}) => {
  const dispatch = useDispatch();
  const putAtualizarCoordenadoresCompleto = useSelector(
    (state: RootState) => state.coordenadoresApi.coordenadores.putAtualizarCoordenadoresCompleto
  );
  
  const [coordenadores, setCoordenadores] = useState<Array<ParticipanteResponse>>([]);
  const [coordenadorLider, setCoordenadorLider] = useState<ParticipanteResponse | null>(null);
  const [allCoordenadoresNull, setAllCoordenadoresNull] = useState<boolean>(true);
  
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
  const [pendingCoordenadores, setPendingCoordenadores] = useState<Array<ParticipanteResponse> | null>(null);
  const [pendingCoordenadorLider, setPendingCoordenadorLider] = useState<ParticipanteResponse | null>(null);
  const [localDelimitacaoEfetuada, setLocalDelimitacaoEfetuada] = useState<boolean>(delimitacaoEfetuada);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  
  const updateCoordenadoresCompleto = async (
    codigo: string,
    coordenadoresIds: string[],
    coordenadorLiderId?: string,
    skipAlerts: boolean = false
  ) => {
    try {
      if (!codigo) {
        console.error('codigo is required');
        return;
      }

      const request: AtualizarCoordenadoresRequest = {
        coordenadores: coordenadoresIds.map(id => ({ coordenadorId: id })),
        coordenadorLiderId: coordenadorLiderId
      };

      const result = await putAtualizarCoordenadoresCompleto(
        dispatch,
        codigo,
        request
      );
      
      if (!skipAlerts) {
        if (result && result.mensagem) {
          dispatch(addAlert({ message: result.mensagem, severity: 'warning' }));
        } else {
          dispatch(addAlert({ message: 'Coordenadores atualizados com sucesso', severity: 'success' }));
        }
      }
      
      return result;
    } catch (error: any) {
      console.error('Error updating coordinators:', error);
      
      if (!skipAlerts) {
        let errorMessage = '';
        
        if (error?.response?.data?.mensagem) {
          errorMessage = error.response.data.mensagem;
        } else if (error?.mensagem) {
          errorMessage = error.mensagem;
        } else if (error?.message && !error.message.includes('status code')) {
          errorMessage = error.message;
        }
        
        const detailedMessage = errorMessage
          ? `Erro ao atualizar coordenadores: ${errorMessage}`
          : 'Erro ao atualizar coordenadores';
          
        dispatch(addAlert({ message: detailedMessage, severity: 'error' }));
      }
      
      throw error;
    }
  };

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [initialCoordenadores, setInitialCoordenadores] = useState<string[]>([]);
  const [initialCoordenadorLider, setInitialCoordenadorLider] = useState<string | null>(null);
  const [hasUserChanges, setHasUserChanges] = useState<boolean>(false);
  
  useEffect(() => {
    setIsEditMode(!!codigo);
  }, [codigo]);
  
  useEffect(() => {
    setLocalDelimitacaoEfetuada(delimitacaoEfetuada);
  }, [delimitacaoEfetuada]);
  
  useEffect(() => {
    if (coordenadoresOptions.length > 0) {
      if (coordenadoresIds.length > 0) {
        const filteredCoordenadores = coordenadoresOptions.filter((c) => 
          coordenadoresIds.some((id) => id === c.id)
        );
        setCoordenadores(filteredCoordenadores);
        
        setInitialCoordenadores(filteredCoordenadores.map(c => c.id));
        
        setAllCoordenadoresNull(filteredCoordenadores.length === 0);
      } else {
        setAllCoordenadoresNull(true);
      }
      
      if (coordenadorLiderId) {
        const leader = coordenadoresOptions.find((c) => c.id === coordenadorLiderId) ?? null;
        setCoordenadorLider(leader);
        setInitialCoordenadorLider(leader?.id || null);
      }
      
      setInitialLoad(false);
    }
  }, [coordenadoresOptions, coordenadoresIds, coordenadorLiderId]);
  
  useEffect(() => {
    if (initialLoad) return;
    
    const currentCoordenadoresIds = coordenadores.map(c => c.id);
    const currentCoordenadorLiderId = coordenadorLider?.id || null;
    
    const coordenadoresChanged = 
      currentCoordenadoresIds.length !== initialCoordenadores.length ||
      !currentCoordenadoresIds.every(id => initialCoordenadores.includes(id));
    
    const leaderChanged = currentCoordenadorLiderId !== initialCoordenadorLider;
    
    if ((coordenadoresChanged || leaderChanged) && hasUserChanges && isEditMode && codigo && !allCoordenadoresNull) {
      if (!localDelimitacaoEfetuada) {
        const updateCoordinatorsInBackend = async () => {
          try {
            await updateCoordenadoresCompleto(
              codigo,
              currentCoordenadoresIds,
              currentCoordenadorLiderId || undefined
            );
            
            setInitialCoordenadores(currentCoordenadoresIds);
            setInitialCoordenadorLider(currentCoordenadorLiderId);
          } catch (error) {
            console.error('Error updating coordinators:', error);
          }
        };
        
        updateCoordinatorsInBackend();
      }
    }
  }, [coordenadores, coordenadorLider, isEditMode, codigo, initialLoad, hasUserChanges, localDelimitacaoEfetuada]);

  const handleCoordenadorLider = async () => {
    if (coordenadores.length === 1) {
      const newCoordenadorLider = coordenadores[0];
      setCoordenadorLider(newCoordenadorLider);
      updateField({ name: 'coordenadorLiderId', value: newCoordenadorLider.id });
      setHasUserChanges(true);
    }
  };

  const handleCoordenadorLiderChange = async (value: ParticipanteResponse | null) => {
    if (localDelimitacaoEfetuada && isEditMode) {
      setPendingCoordenadorLider(value);
      setShowConfirmationDialog(true);
    } else {
      setCoordenadorLider(value);
      updateField({ name: 'coordenadorLiderId', value: value?.id ?? '' });
      setHasUserChanges(true);
    }
  };

  const handleCoordenadoresChange = async (value: Array<ParticipanteResponse>) => {
    if (localDelimitacaoEfetuada && isEditMode) {
      setPendingCoordenadores(value);
      setShowConfirmationDialog(true);
    } else {
      updateCoordenadores(value);
    }
  };
  
  const updateCoordenadores = (value: Array<ParticipanteResponse>) => {
    setCoordenadores(value);
    updateField({ name: 'coordenadoresIds', value: value.map((v) => v.id) });
    
    setAllCoordenadoresNull(value.length === 0);
    
    setHasUserChanges(true);
    
    if (coordenadorLider && !value.some((c) => c.id === coordenadorLider.id)) {
      setCoordenadorLider(null);
      updateField({ name: 'coordenadorLiderId', value: '' });
    }
  };
  
  const handleConfirmDialogClose = () => {
    setShowConfirmationDialog(false);
    setPendingCoordenadores(null);
    setPendingCoordenadorLider(null);
  };
  
  const handleConfirmDialogConfirm = async () => {
    setIsConfirmLoading(true);
    
    const originalCoordenadores = [...coordenadores];
    const originalCoordenadorLider = coordenadorLider;
    
    if (isEditMode && codigo) {
      const currentCoordenadoresIds = pendingCoordenadores ? 
        pendingCoordenadores.map(c => c.id) : 
        coordenadores.map(c => c.id);
      
      const currentCoordenadorLiderId = pendingCoordenadorLider ? 
        pendingCoordenadorLider.id : 
        coordenadorLider?.id || null;
      
      try {
        if (pendingCoordenadores !== null) {
          updateCoordenadores(pendingCoordenadores);
        }
        
        if (pendingCoordenadorLider !== null) {
          setCoordenadorLider(pendingCoordenadorLider);
          updateField({ name: 'coordenadorLiderId', value: pendingCoordenadorLider?.id ?? '' });
          setHasUserChanges(true);
        }
        
        const result = await updateCoordenadoresCompleto(
          codigo,
          currentCoordenadoresIds,
          currentCoordenadorLiderId || undefined,
          true
        );
        
        setInitialCoordenadores(currentCoordenadoresIds);
        setInitialCoordenadorLider(currentCoordenadorLiderId);
        
        setLocalDelimitacaoEfetuada(false);
        
        dispatch(addAlert({ message: 'Coordenadores atualizados com sucesso', severity: 'success' }));
        
        setShowConfirmationDialog(false);
        setPendingCoordenadores(null);
        setPendingCoordenadorLider(null);
      } catch (error: any) {
        console.error('Error updating coordinators:', error);
        
        setCoordenadores(originalCoordenadores);
        updateField({ name: 'coordenadoresIds', value: originalCoordenadores.map((v) => v.id) });
        
        setCoordenadorLider(originalCoordenadorLider);
        updateField({ name: 'coordenadorLiderId', value: originalCoordenadorLider?.id ?? '' });
        
        let errorMessage = '';
        
        if (error?.response?.data?.mensagem) {
          errorMessage = error.response.data.mensagem;
        } else if (error?.mensagem) {
          errorMessage = error.mensagem;
        } else if (error?.message && !error.message.includes('status code')) {
          errorMessage = error.message;
        }
        
        const detailedMessage = errorMessage
          ? `Erro ao atualizar coordenadores: ${errorMessage}. As alterações foram revertidas.`
          : 'Erro ao atualizar coordenadores. As alterações foram revertidas.';
          
        dispatch(addAlert({ message: detailedMessage, severity: 'error' }));
        
        setShowConfirmationDialog(false);
        setPendingCoordenadores(null);
        setPendingCoordenadorLider(null);
      } finally {
        setIsConfirmLoading(false);
      }
    } else {
      try {
        if (pendingCoordenadores !== null) {
          updateCoordenadores(pendingCoordenadores);
        }
        
        if (pendingCoordenadorLider !== null) {
          setCoordenadorLider(pendingCoordenadorLider);
          updateField({ name: 'coordenadorLiderId', value: pendingCoordenadorLider?.id ?? '' });
          setHasUserChanges(true);
        }
        
        setShowConfirmationDialog(false);
        setPendingCoordenadores(null);
        setPendingCoordenadorLider(null);
      } finally {
        setIsConfirmLoading(false);
      }
    }
  };

  if (!showDistribuicaoPublica) return null;

  return (
    <>
      <CoordenadoresConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleConfirmDialogClose}
        onConfirm={handleConfirmDialogConfirm}
        isLoading={isConfirmLoading}
      />
      <Section title="Distribuição pública" defaultGrid>
      <Dropdown
        label={labels.tipoDistribuicaoPublica}
        required
        options={tipoDistribuicaoPublica}
        value={caracteristicas.tipoDistribuicaoPublica.value}
        onChange={(value) => {
          const tipoDistribuicaoPublicaVal =
            tipoDistribuicaoPublica.find((s) => s.value === value) ?? tipoDistribuicaoPublica[0];
          updateField({
            name: 'tipoDistribuicaoPublica',
            value: tipoDistribuicaoPublicaVal,
          });
        }}
        error={showError(labels.tipoDistribuicaoPublica)}
      />
      <DatePicker
        label={labels.dataEncerramentoDistribuicao}
        value={dataEncerramentoDistribuicao}
        onChange={(value) => updateField({ name: 'dataEncerramentoDistribuicao', value })}
      />
      <AutoComplete
        multiple
        options={coordenadoresOptions}
        getOptionLabel={(option: ParticipanteResponse) => addCpfCnpjMask(option.cnpj)}
        filterOptions={(options: Array<ParticipanteResponse>, state) =>
          options.filter((_) => stripCpfCnpjFormatting(_.cnpj).includes(stripCpfCnpjFormatting(state.inputValue)))
        }
        value={coordenadores}
        onChange={(newValue) => handleCoordenadoresChange([...newValue])}
        onBlur={() => handleCoordenadorLider()}
        filterSelectedOptions
        label={`${labels.coordenadores} *`}
        placeholder={'Pesquisar...'}
        renderOption={(option: ParticipanteResponse) => (
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex' }}>
              <Typography style={{ margin: 'auto 12px' }}>{addCpfCnpjMask(option.cnpj)}</Typography>
            </div>
            <div>
              <Typography style={{ margin: 'auto 12px' }} variant="body2">
                {option.razaoSocial}
              </Typography>
            </div>
          </div>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option: ParticipanteResponse, index) => (
            <Chip label={option.razaoSocial} {...getTagProps({ index })} />
          ))
        }
        renderInput={(params) => (
          <MUITextField
            {...params}
            label={`${labels.coordenadores} *`}
            InputProps={{ ...params.InputProps }}
            variant="outlined"
            placeholder={'Pesquisar...'}
            error={showError(labels.coordenadores)}
          />
        )}
      />
      <AutoComplete
        label={`${labels.coordenadorLider} *`}
        options={coordenadores}
        getOptionLabel={(option: ParticipanteResponse) => addCpfCnpjMask(option.cnpj)}
        value={coordenadorLider}
        onChange={(newValue) => handleCoordenadorLiderChange(newValue)}
        disabled={coordenadores.length === 0}
        filterOptions={(options: Array<ParticipanteResponse>, state) =>
          options.filter((_) => stripCpfCnpjFormatting(_.cnpj).includes(stripCpfCnpjFormatting(state.inputValue)))
        }
        renderOption={(option: ParticipanteResponse) => (
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex' }}>
              <Typography style={{ margin: 'auto 12px' }}>{addCpfCnpjMask(option.cnpj)}</Typography>
            </div>
            <div>
              <Typography style={{ margin: 'auto 12px' }} variant="body2">
                {option.razaoSocial}
              </Typography>
            </div>
          </div>
        )}
        renderInput={(params) => (
          <MUITextField
            {...params}
            label={`${labels.coordenadorLider} *`}
            InputProps={{ ...params.InputProps }}
            variant="outlined"
            error={showError(labels.coordenadorLider)}
          />
        )}
        helperText={coordenadorLider?.razaoSocial}
      />
      <ParticipanteAutoComplete
        label={`${labels.escriturador} *`}
        participantes={escrituradores}
        participante={escriturador}
        onChange={handleEscrituradorChange}
        error={showError(labels.escriturador)}
        disabled={escrituradorDisabled}
      />

      <Dropdown
        label={labels.contraparteRecebedora}
        required
        options={contraparteRecebedora}
        value={caracteristicas.contraparteRecebedora.value}
        onChange={(value) => {
          const contraparteRecebedoraVal =
            contraparteRecebedora.find((s) => s.value === value) ?? contraparteRecebedora[0];
          updateField({
            name: 'contraparteRecebedora',
            value: contraparteRecebedoraVal,
          });
        }}
        error={showError(labels.contraparteRecebedora)}
      />
    </Section>
    </>
  );
};

export default DistribuicaoPublicaSection;